/**
 * The logic behind the customer card's "Prekės" tab, without React: which
 * dates each period button stands for, and how the rows the server returns for
 * that period become the table.
 */
import { CustomerProductsResponse } from "types/CustomerProducts";

// --------------------------------------------------------------- periods ---

/** A calendar day as "YYYY-MM-DD" — the form the server takes and the date inputs give. */
export type Day = string;

/** Both ends included. */
export interface DayRange {
  from: Day;
  to: Day;
}

export type PresetId =
  | "thisMonth"
  | "lastMonth"
  | "thisYear"
  | "lastYear"
  | "last12Months"
  | "all"
  | "custom";

/** The buttons, left to right. */
export const PRESETS: { id: PresetId; labelKey: string }[] = [
  { id: "thisMonth", labelKey: "customer_products_preset_this_month" },
  { id: "lastMonth", labelKey: "customer_products_preset_last_month" },
  { id: "thisYear", labelKey: "customer_products_preset_this_year" },
  { id: "lastYear", labelKey: "customer_products_preset_last_year" },
  { id: "last12Months", labelKey: "customer_products_preset_last_12_months" },
  { id: "all", labelKey: "customer_products_preset_all" },
  { id: "custom", labelKey: "customer_products_preset_custom" },
];

/** What is chosen. Saved between visits, so it is plain data; the dates of a preset are never saved. */
export interface PeriodChoice {
  preset: PresetId;
  /** The range typed in by "custom"; used only when `preset` is "custom". */
  customFrom: Day;
  customTo: Day;
}

export const WHOLE_HISTORY: PeriodChoice = { preset: "all", customFrom: "", customTo: "" };

const pad = (number: number) => String(number).padStart(2, "0");

/** The day on the user's own calendar. toISOString() would give the UTC day, which is the wrong one in the first or last hours of a local day. */
export const toDay = (date: Date): Day =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** A real calendar day in YYYY-MM-DD form; "2026-02-31" is not one, though it has the shape. */
const isDay = (value: unknown): value is Day => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  return toDay(new Date(year, month - 1, day)) === value;
};

/** Whether "Rodyti" may be pressed: both days are set and the first is not after the second. */
export const isCustomRangeValid = (from: string, to: string): boolean =>
  isDay(from) && isDay(to) && from <= to;

/** The days a choice stands for on `today`; null means the whole history. */
export function rangeOf(choice: PeriodChoice, today: Date): DayRange | null {
  const year = today.getFullYear();
  const month = today.getMonth();
  // new Date(year, month - 1, 1) and new Date(year, month + 1, 0) roll over year ends by themselves.
  switch (choice.preset) {
    case "thisMonth":
      return { from: toDay(new Date(year, month, 1)), to: toDay(today) };
    case "lastMonth":
      return { from: toDay(new Date(year, month - 1, 1)), to: toDay(new Date(year, month, 0)) };
    case "thisYear":
      return { from: toDay(new Date(year, 0, 1)), to: toDay(today) };
    case "lastYear":
      return { from: toDay(new Date(year - 1, 0, 1)), to: toDay(new Date(year - 1, 11, 31)) };
    case "last12Months":
      // Twelve calendar months including the current one.
      return { from: toDay(new Date(year, month - 11, 1)), to: toDay(today) };
    case "custom":
      return { from: choice.customFrom, to: choice.customTo };
    case "all":
      return null;
  }
}

/**
 * The saved choice to use now. Anything that is not a choice of this shape
 * (nothing saved, or the month strip's older format) starts from the whole history.
 */
export function restoreChoice(saved: unknown): PeriodChoice {
  const choice = saved as Partial<PeriodChoice> | null;
  const isKnownPreset = PRESETS.some((preset) => preset.id === choice?.preset);
  if (!choice || !isKnownPreset) return WHOLE_HISTORY;
  if (typeof choice.customFrom !== "string" || typeof choice.customTo !== "string") {
    return WHOLE_HISTORY;
  }
  if (choice.preset === "custom" && !isCustomRangeValid(choice.customFrom, choice.customTo)) {
    return WHOLE_HISTORY;
  }
  return choice as PeriodChoice;
}

// ----------------------------------------------------------------- table ---

const roundTo = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  // "+ 0" turns a rounded -0 into 0, which would otherwise print as "-0.00".
  return Math.round(value * factor) / factor + 0;
};

/** One product's line in the table, summed over the returned months. */
export interface ProductRow {
  /** The product id; also the table's row key. */
  id: string;
  name: string;
  sku: string | null;
  supplierSku: string | null;
  /** Unit of measure of `quantity`; null = none known. */
  unit: string | null;
  quantity: number;
  amountEur: number;
  returnedEur: number;
  invoiceCount: number;
}

type ProductSums = Pick<ProductRow, "quantity" | "amountEur" | "returnedEur" | "invoiceCount">;

/**
 * All returned months folded per product (the server has already cut them to
 * the period). A product whose quantity, amount and returned amount all come
 * to 0 is left out; the rest are sorted by net amount, largest first.
 */
export function productRowsOf(data: CustomerProductsResponse): ProductRow[] {
  const sums: Record<string, ProductSums> = {};
  for (const row of data.months) {
    const sum = sums[row.productId] || { quantity: 0, amountEur: 0, returnedEur: 0, invoiceCount: 0 };
    sum.quantity += row.quantity;
    sum.amountEur += row.amountEur;
    sum.returnedEur += row.returnedEur;
    sum.invoiceCount += row.invoiceCount;
    sums[row.productId] = sum;
  }

  const rows: ProductRow[] = [];
  for (const product of data.products) {
    const sum = sums[product.productId];
    if (!sum) continue;
    const quantity = roundTo(sum.quantity, 3);
    const amountEur = roundTo(sum.amountEur, 2);
    const returnedEur = roundTo(sum.returnedEur, 2);
    if (quantity === 0 && amountEur === 0 && returnedEur === 0) continue;
    rows.push({
      id: product.productId,
      name: product.name,
      sku: product.sku,
      supplierSku: product.supplierSku,
      // An older server does not send the unit yet.
      unit: product.unit ?? null,
      quantity,
      amountEur,
      returnedEur,
      invoiceCount: sum.invoiceCount,
    });
  }
  return rows.sort((a, b) => b.amountEur - a.amountEur);
}

/** Rows whose name, code or supplier code contains the text, ignoring case. */
export function filterRowsBySearch(rows: ProductRow[], search: string): ProductRow[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) =>
    [row.name, row.sku, row.supplierSku].some((text) => !!text && text.toLowerCase().includes(needle))
  );
}

/** How much was bought in one unit of measure; `unit` null = products without a unit. */
export interface QuantityOfUnit {
  unit: string | null;
  quantity: number;
}

export interface PeriodTotals {
  productCount: number;
  netEur: number;
  returnedEur: number;
  /**
   * The quantity bought per unit of measure — tonnes are never added to
   * pieces. Units are listed in the order they first appear in the rows (the
   * biggest-selling product's unit first); a unit whose quantities cancel out
   * is left out.
   */
  quantityByUnit: QuantityOfUnit[];
}

export function totalsOf(rows: ProductRow[]): PeriodTotals {
  return {
    productCount: rows.length,
    netEur: roundTo(rows.reduce((sum, row) => sum + row.amountEur, 0), 2),
    returnedEur: roundTo(rows.reduce((sum, row) => sum + row.returnedEur, 0), 2),
    quantityByUnit: quantityByUnitOf(rows),
  };
}

function quantityByUnitOf(rows: ProductRow[]): QuantityOfUnit[] {
  const sums: QuantityOfUnit[] = [];
  for (const row of rows) {
    const sumOfUnit = sums.find((sum) => sum.unit === row.unit);
    if (sumOfUnit) sumOfUnit.quantity += row.quantity;
    else sums.push({ unit: row.unit, quantity: row.quantity });
  }
  return sums
    .map(({ unit, quantity }) => ({ unit, quantity: roundTo(quantity, 3) }))
    .filter(({ quantity }) => quantity !== 0);
}

/** A quantity as people read it: up to 3 decimals, so tonnes keep their kilograms ("20.125"). */
export const formatQuantity = (quantity: number): string =>
  quantity.toLocaleString("en-US", { maximumFractionDigits: 3 });

/** The unit all the rows are measured in, so their quantities can be added up; null when they differ, when one has no unit, or when there are no rows. */
export function commonUnitOf(rows: ProductRow[]): string | null {
  const firstUnit = rows.length > 0 ? rows[0].unit : null;
  return rows.every((row) => row.unit === firstUnit) ? firstUnit : null;
}
